import { useState, useEffect } from 'react';
import { formatKwd } from '@/lib/formatPrice';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShoppingBasket, Trash2, Minus, Plus, ArrowRight, X } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useLocale } from '@/contexts/LocaleContext';
import { supabase } from '@/integrations/supabase/client';
import { optimizedImageUrl } from '@/lib/storage';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

const HIDE_ON = ['/basket', '/checkout', '/auth', '/signup'];

export default function FloatingCart() {
  const { items, count, updateQuantity, removeItem, clear } = useCart();
  const { locale, t } = useLocale();
  const isArabic = locale === 'ar';
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [prevCount, setPrevCount] = useState(count);

  // Pulse animation when an item is added
  useEffect(() => {
    if (count > prevCount) {
      setPulse(true);
      const id = setTimeout(() => setPulse(false), 700);
      return () => clearTimeout(id);
    }
    setPrevCount(count);
  }, [count, prevCount]);

  const hidden = HIDE_ON.some(p => location.pathname.startsWith(p));
  const ids = items.map(i => i.activityId);

  const { data: activities = [] } = useQuery({
    queryKey: ['floating-cart-activities', ids.sort().join(',')],
    queryFn: async () => {
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from('activities')
        .select('id, title_ar, title_en, price_kwd, extra_price_per_person_kwd, min_participants, gallery_urls')
        .in('id', ids);
      if (error) throw error;
      return data || [];
    },
    enabled: open && ids.length > 0,
  });

  const getTitle = (a: any) => isArabic ? a.title_ar : (a.title_en || a.title_ar);
  const lineItems = items.map(ci => {
    const a = activities.find(x => x.id === ci.activityId);
    if (!a) return null;
    const minP = a.min_participants || 1;
    const extra = Math.max(0, ci.quantity - minP);
    const subtotal = (a.price_kwd || 0) + (a.extra_price_per_person_kwd || 0) * extra;
    return { ci, a, subtotal };
  }).filter(Boolean) as { ci: any; a: any; subtotal: number }[];
  const total = lineItems.reduce((s, l) => s + l.subtotal, 0);

  if (hidden || count === 0) return null;

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={isArabic ? 'فتح السلة' : 'Open basket'}
        className={`fixed bottom-24 ${isArabic ? 'left-4 md:left-6' : 'right-4 md:right-6'} z-[70] flex items-center justify-center w-14 h-14 rounded-full bg-gradient-cta text-primary-foreground shadow-elevated hover:scale-105 active:scale-95 transition-transform ${pulse ? 'animate-bounce' : ''}`}
      >
        <ShoppingBasket className="w-6 h-6" />
        <span className="absolute -top-1 -end-1 min-w-[20px] h-[20px] px-1 rounded-full bg-background text-foreground text-[11px] font-bold flex items-center justify-center shadow">
          {count > 99 ? '99+' : count}
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side={isArabic ? 'left' : 'right'} className="w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="px-5 py-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2">
              <ShoppingBasket className="w-5 h-5" />
              {isArabic ? `سلتك (${count})` : `Your basket (${count})`}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {lineItems.length === 0 ? (
              <div className="text-center text-muted-foreground py-10 text-sm">
                {isArabic ? 'جارٍ التحميل...' : 'Loading...'}
              </div>
            ) : lineItems.map(({ ci, a, subtotal }) => {
              const img = a.gallery_urls?.[0] || 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=400';
              return (
                <div key={a.id} className="flex gap-3 p-2 rounded-2xl bg-white/50 backdrop-blur-xl border border-white/60">
                  <Link to={`/activities/${a.id}`} onClick={() => setOpen(false)} className="shrink-0">
                    <img src={optimizedImageUrl(img, { width: 160, quality: 70 })} alt={getTitle(a)} className="w-20 h-20 rounded-xl object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <Link to={`/activities/${a.id}`} onClick={() => setOpen(false)} className="font-bold text-sm line-clamp-2 hover:text-primary">
                        {getTitle(a)}
                      </Link>
                      <button onClick={() => removeItem(a.id)} aria-label={isArabic ? 'إزالة' : 'Remove'} className="p-1 rounded-full hover:bg-destructive/10 text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-1 rounded-full border border-border bg-background">
                        <button onClick={() => updateQuantity(a.id, Math.max(1, ci.quantity - 1))} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-muted" aria-label="-">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="min-w-[24px] text-center text-xs font-bold">{ci.quantity}</span>
                        <button onClick={() => updateQuantity(a.id, ci.quantity + 1)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-muted" aria-label="+">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-sm font-bold text-primary">{formatKwd(subtotal)} {t('kwd')}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-border p-4 space-y-3 bg-background">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{isArabic ? 'الإجمالي' : 'Total'}</span>
              <span className="text-xl font-bold text-primary">{formatKwd(total)} {t('kwd')}</span>
            </div>
            <Button
              onClick={() => { setOpen(false); navigate('/checkout'); }}
              className="w-full rounded-full bg-gradient-cta text-primary-foreground h-12 shadow-md hover:opacity-90"
              disabled={lineItems.length === 0}
            >
              {isArabic ? 'متابعة لإتمام الحجز' : 'Continue to checkout'}
              <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
            </Button>
            <div className="flex items-center justify-between text-xs">
              <Link to="/basket" onClick={() => setOpen(false)} className="text-primary hover:underline">
                {isArabic ? 'فتح السلة الكاملة' : 'Open full basket'}
              </Link>
              <button onClick={clear} className="text-muted-foreground hover:text-destructive underline">
                {isArabic ? 'إفراغ السلة' : 'Clear'}
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
